import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { scalePx, viewSizeCalculator } from "../../utils/index";
import styled from "styled-components";

/**
 * Shared line chart used across the app. Handles viewport/zoom-consistent
 * sizing (via scalePx + a resize listener) so every chart behaves the same.
 *
 * @param {Array<{name: string|number, value: number|string}>} data - pre-shaped chart data
 * @param {string} [title] - optional heading rendered above the chart
 * @param {number} [heightVw=10] - wrapper height expressed in viewSizeCalculator units
 * @param {boolean} [showYAxis=false] - render the Y axis
 * @param {boolean} [showDots=true] - render dots on each data point
 * @param {(value:any)=>any} [tooltipFormatter] - custom tooltip value formatter
 * @param {(value:any)=>any} [yAxisFormatter] - custom Y axis tick formatter
 * @param {string} [stroke="#E35B42"] - line color
 */
const BaseLineChart = ({
  data = [],
  title,
  heightVw = 10,
  showYAxis = false,
  showDots = true,
  tooltipFormatter,
  yAxisFormatter,
  stroke = "#E35B42",
}) => {
  // Re-render on resize/zoom so vw-based and px-based scaling stay in sync.
  const [, forceResize] = useState(0);
  useEffect(() => {
    const onResize = () => forceResize((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <Wrapper $heightVw={heightVw}>
      {title && <Title>{title}</Title>}
      <ResponsiveContainer width={"100%"} height={"100%"}>
        <LineChart
          data={data}
          margin={{
            top: scalePx(1.25),
            right: scalePx(0.5),
            bottom: scalePx(0.25),
            left: scalePx(0.5),
          }}
          accessibilityLayer
        >
          <CartesianGrid strokeDasharray="3 3" strokeWidth={scalePx(0.0625)} />
          <XAxis
            tick={{ fontSize: viewSizeCalculator(0.8) }}
            dataKey="name"
            interval="preserveStartEnd"
            minTickGap={scalePx(2)}
            padding={{ left: scalePx(1.25), right: scalePx(1.25) }}
          />
          {showYAxis && (
            <YAxis
              tick={{ fontSize: viewSizeCalculator(0.8) }}
              tickFormatter={yAxisFormatter}
            />
          )}
          <Tooltip
            formatter={tooltipFormatter}
            contentStyle={{
              borderRadius: viewSizeCalculator(0.5),
              fontSize: viewSizeCalculator(1.125),
              padding: viewSizeCalculator(0.5),
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={scalePx(0.125)}
            dot={showDots ? { r: scalePx(0.25), strokeWidth: scalePx(0.0625) } : false}
            activeDot={{ r: scalePx(0.5) }}
          ></Line>
        </LineChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};

export default BaseLineChart;

const Wrapper = styled.div`
  width: 100%;
  height: ${({ $heightVw }) => `${viewSizeCalculator($heightVw)}`};

  /* Remove the black focus outline recharts adds to the chart surface on click */
  .recharts-wrapper,
  .recharts-surface,
  svg,
  *:focus {
    outline: none;
  }
`;

const Title = styled.h4`
  font-size: ${() => `${viewSizeCalculator(1.1)}`};
  font-weight: 700;
  padding-left: ${() => `${viewSizeCalculator(1)}`};
`;
